import ReactDOM from "react-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { APIClient } from "api/apiCore";
import * as url from "api/urls"
import { backdropStyle, modalStyle } from "DialogProviderShare";
import { Utils } from "helpers/utils";
import { tb_HookData, tb_Hook, HookEvents, HookBodyTypes, HookMethods, HookAuths, HookDataSection, HookFilterData } from "types";
import AppSimpleBar from "components/AppSimpleBar";
import { Checkbox, FormControlLabel, Switch } from "@mui/material";
import Loader from "components/Loader";
import LoadingIconWithText from "components/LoadingIconWithText";
import ReactJson from "react-json-view";
import { createSelector } from "reselect";
import { useRedux } from "hooks";
import { SettingsState } from "redux/settings/types";

// 1. Create context at module level
const ColorContext = React.createContext<ReturnType<typeof useColors>>(null!);
const useC = () => React.useContext(ColorContext);

// 2. baseInput becomes a function that takes C
const makeBaseInput = (C: ReturnType<typeof useColors>): React.CSSProperties => ({
  background: C.bg,
  border: `1px solid ${C.border}`,
  borderRadius: 7,
  color: C.text,
  padding: "9px 12px",
  outline: "none",
  width: "100%",
  fontFamily: font,
  boxSizing: "border-box",
  transition: "border-color 0.15s",
});

// ── Design tokens ──────────────────────────────────────────────────────────
function useColors(isDark: boolean) {
  return isDark ? {
    bg: "#09090b",
    surface: "#111113",
    border: "#27272a",
    borderFocus: "#a78bfa",
    text: "#fafafa",
    muted: "#71717a",
    subtle: "#3f3f46",
    placeholder: "#52525b",
    accent: "#a78bfa",
    accentDim: "#a78bfa22",
    green: "#34d399",
    greenDim: "#34d39922",
    red: "#f87171",
    redDim: "#f8717122",
    amber: "#fbbf24",
    amberDim: "#fbbf2422",
  } : {
    bg: "#ffffff",
    surface: "#f8fafc",
    border: "#e2e8f0",
    borderFocus: "#7c3aed",
    text: "#0f172a",
    muted: "#94a3b8",
    subtle: "#cbd5e1",
    placeholder: "#a8b3c4",
    accent: "#7c3aed",
    accentDim: "#7c3aed18",
    green: "#059669",
    greenDim: "#05966918",
    red: "#dc2626",
    redDim: "#dc262618",
    amber: "#d97706",
    amberDim: "#d9770618",
  };
}

const font = "inherit";

// ── Tabs ───────────────────────────────────────────────────────────────────
const TABS = [
  { id: "general", label: "General", icon: "⚙" },
  { id: "header", label: "Header", icon: "◈" },
  { id: "body", label: "Body", icon: "{}" },
  { id: "test", label: "Test", icon: "#" }
] as const;
type TabId = typeof TABS[number]["id"];

// ── Styled Input ───────────────────────────────────────────────────────────
function StyledInput(props: React.InputHTMLAttributes<HTMLInputElement> & { icon?: string }) {
  const C = useC();                          // 👈 from context
  const baseInput = makeBaseInput(C);        // 👈 derived here  
  const [f, setF] = useState(false);
  const { icon, style, ...rest } = props;
  return (
    <div style={{ position: "relative" }}>
      {icon && (
        <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", /*fontSize: 13,*/ color: C.muted, pointerEvents: "none" }}>
          {icon}
        </span>
      )}
      <input
        {...rest}
        style={{ ...baseInput, borderColor: f ? C.borderFocus : C.border, paddingLeft: icon ? 32 : 12, ...style }}
        onFocus={e => { setF(true); props.onFocus?.(e); }}
        onBlur={e => { setF(false); props.onBlur?.(e); }}
      />
    </div>
  );
}

// ── Styled Select ──────────────────────────────────────────────────────────
function StyledSelect(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const C = useC();                          // 👈 from context
  const baseInput = makeBaseInput(C);        // 👈 derived here  
  const [f, setF] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...baseInput, borderColor: f ? C.borderFocus : C.border, cursor: "pointer",
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2371717a'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: 32,
        ...props.style,
      }}
      onFocus={e => { setF(true); props.onFocus?.(e); }}
      onBlur={e => { setF(false); props.onBlur?.(e); }}
    />
  );
}

function EnumSelect<T extends Record<string, string>>({
  enumObj, value, onChange, placeholder, disabled
}: { enumObj: T; value: string; onChange: (v: string) => void; placeholder?: string, disabled: boolean | undefined }) {
  const C = useC();
  // A select's placeholder is a real <option>, so ::placeholder can't reach it - style the empty
  // state by hand. Option colours come from the dialog's `select option` rule, so only the font
  // style needs resetting for the real options.
  const isEmpty = !value && !!placeholder;
  return (
    <StyledSelect value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      style={isEmpty ? { color: C.placeholder, fontStyle: "italic" } : undefined}>
      {placeholder && <option value="" style={{ color: C.placeholder, fontStyle: "italic" }}>{placeholder}</option>}
      {Object.values(enumObj).map(v => (
        <option key={v} value={v} style={{ fontStyle: "normal" }}>{Utils.SplitByCase(v)}</option>
      ))}
    </StyledSelect>
  );
}

// ── Shared ⓘ popup ─────────────────────────────────────────────────────────
// Its own portal above the dialog. An inline panel would push the form down inside AppSimpleBar
// and could not scroll on its own.
function InfoOverlay({ title, onClose, children, maxWidth = 820 }: { title: string; onClose: () => void; children: React.ReactNode; maxWidth?: number }) {
  const C = useC();
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;

  // Escape closes this popup, not the whole dialog. The dialog listens on window in the bubble
  // phase, so this capture-phase listener runs first and stops the event reaching it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); closeRef.current(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1100,
        display: "flex", justifyContent: "center", alignItems: "center", padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12,
          width: "100%", maxWidth, maxHeight: "85vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 20px 50px rgba(0,0,0,0.45)", fontFamily: font,
        }}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 14px", borderBottom: `1px solid ${C.border}`, flexShrink: 0,
        }}>
          <span style={{ fontSize: "1.0rem", fontWeight: 700 }}>{title}</span>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: "1.1rem", lineHeight: 1, padding: 4 }}
          >✕</button>
        </div>
        <div style={{ overflowY: "auto", padding: "6px 0" }}>{children}</div>
      </div>
    </div>,
    document.body
  );
}

function InfoIcon({ active, onClick, title }: { active: boolean; onClick: () => void; title: string }) {
  const C = useC();
  return (
    <i
      className="fas fa-info-circle ms-1 hand"
      title={title}
      onClick={onClick}
      style={{ color: active ? C.accent : C.muted, cursor: "pointer", textTransform: "none" }}
    />
  );
}

function Field({ label, hint, children, action, info }: { label: string; hint?: string; children: React.ReactNode; action?: React.ReactNode; info?: React.ReactNode }) {
  const C = useC()
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <label style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.07em", color: C.muted }}>
          {label}{info}
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {action && action}
          {hint && <span style={{ fontSize: 11, color: C.subtle }}>{hint}</span>}
        </div>
      </div>
      {children}
    </div>
  );
}

function TabBar({ active, onChange, counts, allowedit }: {
  active: TabId; onChange: (t: TabId) => void; counts: Record<string, number>; allowedit: boolean;
}) {
  const C = useC()
  return (
    <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, marginBottom: 28, gap: 2 }}>
      {TABS.filter(x => allowedit || x.id !== "test").map(tab => {
        const isActive = tab.id === active;
        const count = counts[tab.id] ?? 0;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: font, /*fontSize: 13,*/ fontWeight: isActive ? 700 : 400,
              color: isActive ? C.accent : C.muted,
              padding: "10px 18px",
              borderBottom: isActive ? `2px solid ${C.accent}` : "2px solid transparent",
              marginBottom: -1,
              display: "flex", alignItems: "center", gap: 7,
              transition: "color 0.15s",
            }}
          >
            <span style={{ fontSize: 14 }}>{tab.icon}</span>
            {tab.label}
            {count > 0 && (
              <span style={{
                background: isActive ? C.accentDim : C.border,
                color: isActive ? C.accent : C.muted,
                borderRadius: 20, /*fontSize: 10,*/ fontWeight: 700,
                padding: "1px 7px", lineHeight: "18px",
              }}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Value combo ────────────────────────────────────────────────────────────
// Free-text box with a dropdown of the fields the selected event can supply. One body property
// carries one field, so picking replaces the whole box with {field}. Typing narrows the list.
function ValueCombo({ value, onChange, fields, disabled }: {
  value: string; onChange: (v: string) => void; fields: string[]; disabled: boolean;
}) {
  const C = useC();
  const baseInput = makeBaseInput(C);
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState<"down" | "up">("down");
  const [hi, setHi] = useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const shown = useMemo(() => {
    // Once the box holds a field, stop filtering - otherwise reopening the list would show only
    // the field that is already selected.
    if (fields.some(f => value === "{" + f + "}")) return fields;
    const filter = value.replace(/[{}]/g, "").trim().toLowerCase();
    return filter ? fields.filter(f => f.toLowerCase().includes(filter)) : fields;
  }, [fields, value]);
  const hiSafe = Math.min(hi, Math.max(0, shown.length - 1));

  const openList = () => {
    const r = inputRef.current?.getBoundingClientRect();
    // flip up when there is no room below, otherwise the list is cut off by the scroll area
    setDrop(r && window.innerHeight - r.bottom < 220 ? "up" : "down");
    setHi(0);
    setOpen(true);
  };

  const pick = (field: string) => {
    onChange("{" + field + "}");
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (fields.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) openList(); else setHi(h => Math.min(h + 1, shown.length - 1));
    } else if (e.key === "ArrowUp" && open) {
      e.preventDefault();
      setHi(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && open && shown.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      pick(shown[hiSafe]);
    } else if (e.key === "Escape" && open) {
      // the dialog closes on a window-level Escape - stop it here or dismissing the list would
      // close the whole popup and lose the edit
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  const showClear = !disabled && value.length > 0;
  const showCaret = !disabled && fields.length > 0;
  // leave room for whichever buttons are actually rendered
  const rightPad = 12 + (showClear ? 20 : 0) + (showCaret ? 20 : 0);

  return (
    <div style={{ position: "relative" }}>
      <input
        ref={inputRef}
        placeholder="Value"
        disabled={disabled}
        value={value}
        onChange={e => { onChange(e.target.value); if (fields.length > 0) setOpen(true); }}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); setOpen(false); }}
        style={{
          ...baseInput,
          borderColor: focused ? C.borderFocus : C.border,
          paddingRight: rightPad,
        }}
      />

      {(showClear || showCaret) && (
        <div style={{
          position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)",
          display: "flex", alignItems: "center", gap: 1,
        }}>
          {showClear && (
            <button
              type="button"
              tabIndex={-1}
              title="ล้างข้อความ"
              onMouseDown={e => e.preventDefault()}   // keep the caret in the input
              onClick={() => { onChange(""); setOpen(false); inputRef.current?.focus(); }}
              style={{
                background: "none", border: "none", cursor: "pointer", color: C.muted,
                padding: "2px 4px", lineHeight: 1, fontSize: 12,
              }}
            >✕</button>
          )}
          {showCaret && (
            <button
              type="button"
              tabIndex={-1}
              title="Available fields"
              onMouseDown={e => e.preventDefault()}   // keep the caret in the input
              onClick={() => { if (open) setOpen(false); else { inputRef.current?.focus(); openList(); } }}
              style={{
                background: "none", border: "none", cursor: "pointer", color: C.muted,
                padding: "2px 4px", lineHeight: 1, fontSize: 10,
              }}
            >▼</button>
          )}
        </div>
      )}

      {open && shown.length > 0 && (
        <div
          onMouseDown={e => e.preventDefault()}   // clicking must not blur the input first
          style={{
            position: "absolute", left: 0, right: 0, zIndex: 20,
            [drop === "up" ? "bottom" : "top"]: "calc(100% + 4px)",
            background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
            maxHeight: 200, overflowY: "auto",
            boxShadow: "0 8px 24px rgba(0,0,0,0.28)",
          }}
        >
          {shown.map((f, i) => (
            <div
              key={f}
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(f)}
              style={{
                padding: "6px 10px", cursor: "pointer", fontSize: 12,
                background: i === hiSafe ? C.accentDim : "transparent",
                color: i === hiSafe ? C.accent : C.text,
              }}
            >
              {"{" + f + "}"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── KV Row ─────────────────────────────────────────────────────────────────
function KVRow({ row, onChange, onRemove, disabled, fields }: {
  row: tb_HookData; onChange: (r: tb_HookData) => void; onRemove: () => void; disabled: boolean;
  fields: string[];
}) {
  const C = useC()
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 36px", gap: 8, alignItems: "center" }}>
      <StyledInput placeholder="Key" disabled={disabled} value={row?.name ?? ""} onChange={e => onChange({ ...row, name: e.target.value })} />
      <ValueCombo value={row?.value ?? ""} disabled={disabled} fields={fields} onChange={v => onChange({ ...row, value: v })} />
      {!disabled && (
        <button
          onClick={onRemove}
          style={{
            background: C.redDim, border: `1px solid ${C.red}33`, borderRadius: 7,
            color: C.red, cursor: "pointer", /*fontSize: 14,*/
            width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >✕</button>
      )}
    </div>
  );
}

function KVTable({ rows, onUpdate, onAdd, onRemove, addLabel, disabled, fields = [] }: {
  rows: tb_HookData[]; onUpdate: (i: number, r: tb_HookData) => void;
  onAdd: () => void; onRemove: (i: number) => void; addLabel: string;
  disabled: boolean;
  /** Fields offered in the value dropdown. Empty (the header table) means plain free text. */
  fields?: string[];
}) {
  const C = useC()
  const [showHelp, setShowHelp] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const hdrStyle: React.CSSProperties = { fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.subtle };

  // Copies the placeholder with its braces - that is what gets pasted into a Value box.
  const copyField = (f: string) => {
    const tag = "{" + f + "}";
    try {
      navigator.clipboard?.writeText(tag).catch(() => { });
    } catch { /* clipboard unavailable (insecure origin) - the click just does nothing */ }
    setCopied(f);
    setTimeout(() => setCopied(c => (c === f ? null : c)), 1500);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* header shows whenever there are fields to explain, even before the first row is added */}
      {(rows.length > 0 || fields.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 36px", gap: 8, padding: "0 2px" }}>
          <span style={hdrStyle}>Key</span>
          <span style={hdrStyle}>
            Value
            {fields.length > 0 && (
              <InfoIcon active={showHelp} title="ความหมายของตัวแปรแต่ละตัว" onClick={() => setShowHelp(v => !v)} />
            )}
          </span>
          <span style={hdrStyle}></span>
        </div>
      )}

      {showHelp && fields.length > 0 && (
        <InfoOverlay title={`ตัวแปรที่ใช้ได้ (${fields.length})`} onClose={() => setShowHelp(false)} maxWidth={1080}>
          {/* One grid for every row, so the name and type columns size to their content
              (max-content) and still line up across rows. Per-row grids could not do both. */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "max-content max-content minmax(0, 1fr)",
            columnGap: 14, alignItems: "baseline", fontSize: "1.0rem",
          }}>
            <div style={{
              gridColumn: "1 / -1", padding: "8px 14px 10px", marginBottom: 4,
              borderBottom: `1px dashed ${C.border}`,
              fontSize: "0.9rem", color: C.muted, lineHeight: 1.5,
            }}>
              ค่าทุกตัวถูกส่งออกไปเป็น <b>ข้อความ</b> เสมอ (form-data หรือ JSON body ตามที่ตั้งค่าไว้)
              — ถ้าค่าเป็น null ปลายทางจะได้ <b>ข้อความว่าง</b> ไม่ใช่คำว่า null
            </div>

            {fields.map((f, idx) => {
              const info = Utils.GetHookFieldInfo(f);
              const cell: React.CSSProperties = {
                padding: "7px 0",
                borderTop: idx === 0 ? "none" : `1px solid ${C.border}55`,
              };
              return (
                <React.Fragment key={f}>
                  <div style={{ ...cell, paddingLeft: 14 }}>
                    <span
                      onClick={() => copyField(f)}
                      title="คลิกเพื่อคัดลอก"
                      style={{
                        color: copied === f ? C.green : C.accent, fontWeight: 700,
                        whiteSpace: "nowrap", cursor: "pointer", userSelect: "all",
                      }}
                    >
                      {"{" + f + "}"}
                      <i className={copied === f ? "fas fa-check ms-1" : "far fa-copy ms-1"}
                        style={{ opacity: copied === f ? 1 : 0.45, fontSize: "0.85em" }} />
                    </span>
                  </div>

                  <div style={{ ...cell, color: C.muted, fontStyle: "italic", whiteSpace: "nowrap" }}>
                    {info.type || "-"}
                  </div>

                  <div style={{ ...cell, paddingRight: 14 }}>
                    <div>{info.desc || "-"}</div>
                    {info.values && (
                      <div style={{ marginTop: 3, fontSize: "0.85rem", color: C.muted }}>
                        <span style={{ opacity: 0.8 }}>ค่าที่เป็นไปได้: </span>
                        <span style={{ color: C.amber, fontWeight: 600 }}>{info.values}</span>
                      </div>
                    )}
                    {info.sample && (
                      <div style={{ marginTop: 3, fontSize: "0.85rem", color: C.muted }}>
                        <span style={{ opacity: 0.8 }}>ตัวอย่าง: </span>
                        <span style={{
                          fontFamily: "monospace", color: C.green,
                          background: C.greenDim, borderRadius: 4, padding: "1px 5px",
                          // array/JSON samples are written multi-line - keep the line breaks
                          whiteSpace: "pre-wrap", wordBreak: "break-word",
                          display: info.sample.includes("\n") ? "block" : "inline",
                          marginTop: info.sample.includes("\n") ? 3 : 0,
                        }}>{info.sample}</span>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </InfoOverlay>
      )}

      {rows.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px 0", color: C.subtle, /*fontSize: 12,*/ border: `1px dashed ${C.border}`, borderRadius: 10 }}>
          No entries yet
        </div>
      )}
      {rows.map((row, i) => (
        <KVRow key={row.seq} row={row} onChange={r => onUpdate(i, r)} onRemove={() => onRemove(i)} disabled={disabled} fields={fields} />
      ))}
      {!disabled && (
        <button
          onClick={onAdd}
          style={{
            background: "none", border: `1px dashed ${C.subtle}`, borderRadius: 8,
            color: C.muted, cursor: "pointer", fontFamily: font, /*fontSize: 12,*/ fontWeight: 600,
            padding: "9px 0", width: "100%", letterSpacing: "0.05em",
          }}
        >
          + {addLabel}
        </button>
      )}
    </div>
  );
}

// ── Body Type Picker ───────────────────────────────────────────────────────
function BodyTypePicker({ value, onChange }: { value: HookBodyTypes; onChange: (v: HookBodyTypes) => void }) {
  const C = useC()
  const styles: Record<HookBodyTypes, { bg: string; border: string; color: string }> = {
    [HookBodyTypes.None]: { bg: C.subtle + "33", border: C.subtle, color: C.muted },
    [HookBodyTypes.FormData]: { bg: C.amberDim, border: C.amber, color: C.amber },
    [HookBodyTypes.Json]: { bg: C.greenDim, border: C.green, color: C.green },
  };

  return (
    <div style={{ display: "flex", gap: 8 }}>
      {(Object.values(HookBodyTypes) as HookBodyTypes[]).map(bt => {
        const active = value === bt;
        const s = styles[bt];
        return (
          <button
            key={bt}
            onClick={() => onChange(bt)}
            style={{
              background: active ? s.bg : "none",
              border: `1px solid ${active ? s.border : C.border}`,
              borderRadius: 8, color: active ? s.color : C.muted,
              cursor: "pointer", fontFamily: font, /*fontSize: 12,*/ fontWeight: 700,
              padding: "7px 18px", transition: "all 0.15s", letterSpacing: "0.05em",
            }}
          >
            {Utils.SplitByCase(bt)}
          </button>
        );
      })}
    </div>
  );
}

export function DialogProviderHookInfoPortal({ hookId, onClose }: { hookId: number | null, onClose: (v: any) => void }) {
  const { dispatch, useAppSelector } = useRedux();
  const storeData = createSelector(
    (state: any) => state.Settings,
    (state: any) => state.Profile2,
    (st: SettingsState, p2) => ({
      userConfig: st.userConfig,
      isAdmin: p2.isAdmin,
      curUserId: p2.userId,
    })
  );
  const { isAdmin, userConfig, curUserId } = useAppSelector(storeData);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  let _id = 0;
  const uid = () => ++_id;
  const [tab, setTab] = useState<TabId>("general");
  const [hook, setHook] = useState<tb_Hook>({
    method: HookMethods.POST,
    hookUrl: "",
    authMethod: HookAuths.None,
    bearerToken: "",
    purpose: "",
    event: null,
    bodyType: HookBodyTypes.None,
  });

  const [headers, setHeaders] = useState<tb_HookData[]>([]);
  const [bodyRows, setBodyRows] = useState<tb_HookData[]>([]);
  const [saved, setSaved] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showEventInfo, setShowEventInfo] = useState(false);

  const addHeader = () =>
    setHeaders(r => [...r, { id: uid(), dataSection: HookDataSection.Header, name: "", value: "", seq: r.length + 1 }]);
  const updateHeader = (i: number, row: tb_HookData) => setHeaders(r => r.map((x, idx) => idx === i ? row : x));
  const removeHeader = (i: number) => setHeaders(r => r.filter((_, idx) => idx !== i));

  const addBodyRow = () => {
    const section = hook.bodyType === HookBodyTypes.FormData ? HookDataSection.FormData : HookDataSection.Json;
    setBodyRows(r => [...r, { id: uid(), dataSection: section, name: "", value: "", seq: r.length + 1 }]);
  };
  const updateBodyRow = (i: number, row: tb_HookData) => setBodyRows(r => r.map((x, idx) => idx === i ? row : x));
  const removeBodyRow = (i: number) => setBodyRows(r => r.filter((_, idx) => idx !== i));

  // ── Event filter (kept as a JSON string in tb_hook.jsonfilter) ────────────
  const hasEventFilter = Utils.HookEventHasFilter(hook.event);
  const eventFilter = useMemo(() => Utils.ParseHookFilterData(hook.jsonFilter), [hook.jsonFilter]);
  const autoDescription = eventFilter.autoDescription === true;

  const patchEventFilter = (patch: Partial<HookFilterData>) => setHook(h => ({
    ...h,
    jsonFilter: Utils.BuildHookFilterData({ ...Utils.ParseHookFilterData(h.jsonFilter), ...patch }),
  }));

  const changeEvent = (v: string) => setHook(h => {
    const prev = Utils.ParseHookFilterData(h.jsonFilter);
    // events without extra parameters keep only the auto-description flag
    const filter: HookFilterData = Utils.HookEventHasFilter(v) ? prev : { autoDescription: prev.autoDescription };
    return { ...h, event: v as HookEvents, jsonFilter: Utils.BuildHookFilterData(filter) };
  });

  /**
   * While Auto description is on the description is derived, not stored, so it always follows the
   * event and the filters. Typing in the box switches Auto description off and keeps what was typed.
   */
  const autoDescText = Utils.BuildHookAutoDescription(hook.event, eventFilter);
  const shownPurpose = autoDescription ? autoDescText : (hook.purpose ?? "");
  const changePurpose = (v: string) => setHook(h => ({
    ...h,
    purpose: v,
    jsonFilter: Utils.BuildHookFilterData({ ...Utils.ParseHookFilterData(h.jsonFilter), autoDescription: false }),
  }));

  const hasBody = hook.bodyType === HookBodyTypes.FormData || hook.bodyType === HookBodyTypes.Json;
  // Fields the selected event can supply - offered in the dropdown on every body Value box.
  const availableFields = useMemo(() => Utils.GetAvaliableHookField(hook.event ?? null) ?? [], [hook.event]);
  const tabCounts = { general: 0, header: headers.filter(r => r.name || r.value).length, body: bodyRows.filter(r => r.name || r.value).length };
  const isBearer = hook.authMethod === HookAuths.BearerToken;
  const C = useColors(Utils.IsDarkTheme());
  const [allowEdit, setAllowEdit] = useState<boolean>(false);

  useEffect(() => {
    try {
      setAllowEdit(false);
      if (hookId && hookId > 0) {
        setAllowEdit(false);
        setIsLoading(true);
        new APIClient().fnPostForm(url.HOOK_LOAD, { hookId: hookId })
          .then(rs => {
            // console.log('HOOK_LOAD:', rs);
            if (rs?.hookId) {
              const headers = rs?.hookData?.filter((x: any) => x.dataSection == HookDataSection.Header)?.sort((a: any, b: any) => a?.seq - b?.seq);
              const body = rs?.hookData?.filter((x: any) => x.dataSection == rs.bodyType)?.sort((a: any, b: any) => a?.seq - b?.seq);
              setHook(rs);
              setHeaders(headers);
              setBodyRows(body);
              setAllowEdit(isAdmin || rs.createBy == curUserId || rs.updateBy == curUserId);
            } else {
              alert('Not found hook');
              onClose(null);
            }

            setIsLoading(false);
          })
          .catch(e2 => {
            Utils.HandleException(e2, true);
            setIsLoading(false);
          });
      } else {
        setAllowEdit(true);
        // a new hook starts with auto description on
        setHook({ enabled: true, method: HookMethods.POST, jsonFilter: Utils.BuildHookFilterData({ autoDescription: true }) });
        setHeaders([{ seq: 1, name: "Content-Type", value: "application/json" }]);
        setBodyRows([]);
      }
    } catch (e) {
      Utils.HandleException(e, true);
    }
  }, [hookId]);

  let errMsg = "";
  const err = (msg?: string): null => {
    alert(msg ?? errMsg);
    return null;
  }


  const getDataForSave = (validate: boolean) => {
    const h1 = headers.map((x, idx) => ({ ...x, dataSection: HookDataSection.Header, seq: idx + 1 }));
    const b1 = bodyRows.map((x, idx) => ({ ...x, dataSection: hook.bodyType, seq: idx + 1 }));
    const data = {
      ...hook,
      purpose: shownPurpose,  // auto description is derived, so store what the box shows
      hookData: [...h1, ...b1],
      // header: [...headers],
      // body: [...bodyRows]
    };

    if (validate) {
      if (!data.purpose) return err("กรุณาระบุ Description");
      if (!data.event) return err("กรุณาระบุ Event");
      if (!data.method) return err("กรุณาระบุ Method");
      if (!data.hookUrl) return err("กรุณาระบุ callback url");
      if (data.authMethod == HookAuths.BearerToken && !data.bearerToken) return err("กรุณาระบุ Baerer Token");
    }

    // finaly
    return data;
  }

  const handleSave = () => {
    const data2save = getDataForSave(true);
    // console.log('data2save:', data2save);
    if (!data2save) return;

    setIsLoading(true);
    new APIClient().fnPostBody(url.HOOK_SAVE, data2save)
      .then(rs => {
        const serviceResponse = rs;
        // console.log('hook save:', serviceResponse);
        if (serviceResponse.success) {
          onClose(serviceResponse.data);
        } else {
          alert(serviceResponse.message);
        }

        setIsLoading(false);
      })
      .catch(ex => {
        Utils.HandleException(ex, true);
        setIsLoading(false);
      })

  };

  const [testResult, setTestResult] = useState<any>();
  const [testingHook, setTestingHook] = useState<boolean>(false);
  const fnTestHook = async () => {
    try {
      setTab("test");
      setTestingHook(true);
      const data = getDataForSave(false);
      new APIClient().fnPostBody(url.HOOK_PERFORM, data)
        .then(rs => {
          setTestResult(rs);
          // console.log('HOOK_PERFORM:', rs);
          setTestingHook(false);
        })
        .catch(e2 => {
          Utils.HandleException(e2, true);
          setTestResult(e2);
          setTestingHook(false);
        });

    } catch (e) {
      Utils.HandleException(e, true);
      setTestResult(e);
      setTestingHook(false);
    }
  }

  const fnDel = async () => {
    const rs = await Utils.SweetConfirm("ยืนยันการลบ");
    if (rs.isConfirmed) {
      setIsLoading(true);
      new APIClient().fnPostForm(url.HOOK_DELETE, { hookId: hookId })
        .then(rs => {
          setIsLoading(false);
          onClose(null);
        })
        .catch(er => {
          setIsLoading(false);
          Utils.HandleException(er, true);
        });
    }
  }

  const handleEsc = useCallback((event: any) => {
    if (event.key === "Escape") {
      onClose(-1);
    }
  }, [onClose]);

  useEffect(() => {
    // Add listener when the modal mounts
    window.addEventListener("keydown", handleEsc);

    // Clean up listener when the modal unmounts
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [handleEsc]);

  return ReactDOM.createPortal(
    <ColorContext.Provider value={C}>
      <div style={backdropStyle} >
        {isLoading && (<Loader />)}
        <div style={modalStyle} >

          <div style={{ height: 700, background: C.bg, fontFamily: font, color: C.text, padding: "10px 24px" }} className="">
            <style>{`
        /*@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&display=swap');*/
        * { box-sizing: border-box; }
        /* Placeholder must not pass for a saved value: lighter than any real text, and italic. */
        ::placeholder { color: ${C.placeholder} !important; font-style: italic; font-weight: 400; opacity: 1; }
        select option { background: #18181b; color: #fafafa; }
        @keyframes slideDown { from { opacity:0; transform:translateY(-8px) } to { opacity:1; transform:translateY(0) } }
      `}</style>

            <div style={{ maxWidth: 680, margin: "0 auto" }}>

              {/* Page header */}
              <div style={{ marginBottom: 10, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div>
                  <h2 style={{ /*fontSize: 22,*/ fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>Hook Manager </h2>
                  {/* {allowEdit ? "[edit]" : "[read]"} */}
                </div>

                {/* Live status chips */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", paddingTop: 4 }}>
                  {isBearer && (
                    <span style={{ background: C.accentDim, border: `1px solid ${C.accent}44`, color: C.accent, borderRadius: 6, fontSize: 12, fontWeight: 700, padding: "3px 9px", letterSpacing: "0.06em" }}>
                      🔐 Bearer
                    </span>
                  )}
                  {hook.event && (
                    <span style={{ background: C.greenDim, border: `1px solid ${C.green}44`, color: C.green, borderRadius: 6, fontSize: 12, fontWeight: 700, padding: "3px 9px", letterSpacing: "0.06em" }}>
                      {Utils.SplitByCase(hook.event)}
                    </span>
                  )}
                  {hook.bodyType && hook.bodyType !== HookBodyTypes.None && (
                    <span style={{ background: C.amberDim, border: `1px solid ${C.amber}44`, color: C.amber, borderRadius: 6, fontSize: 12, fontWeight: 700, padding: "3px 9px", letterSpacing: "0.06em" }}>
                      {hook.bodyType}
                    </span>
                  )}
                </div>
              </div>

              {/* Card */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "10px 28px" }}>
                <TabBar active={tab} onChange={setTab} counts={tabCounts} allowedit={allowEdit} />

                <AppSimpleBar style={{ height: 470 }}>
                  {/* ── General ── */}
                  {tab === "general" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 18, animation: "slideDown 0.18s ease" }}>
                      <Field label="Description" action={
                        <div className="d-flex justify-content-end align-items-center w-100" style={{ gap: 8 }}>
                          <FormControlLabel
                            control={<Checkbox size="small" checked={autoDescription} readOnly={!allowEdit} onChange={(e) => allowEdit && patchEventFilter({ autoDescription: e.target.checked })} />}
                            label={"Auto description"} />
                          <FormControlLabel
                            control={<Switch checked={hook.enabled ?? false} readOnly={!allowEdit} onChange={(e) => setHook(h => ({ ...h, enabled: allowEdit ? e.target.checked : h.enabled }))} />}
                            label={"Enabled"} />
                        </div>
                      }
                      >
                        <StyledInput
                          icon="✦"
                          placeholder="Describe what this webhook does…"
                          value={shownPurpose}
                          readOnly={!allowEdit}
                          onChange={e => changePurpose(e.target.value)}
                        />
                      </Field>

                      <Field
                        label="Trigger Event"
                        info={<InfoIcon active={showEventInfo} title="ดูว่าแต่ละ event ยิงตอนไหน" onClick={() => setShowEventInfo(v => !v)} />}
                      >
                        <EnumSelect
                          enumObj={HookEvents}
                          value={hook.event ?? ""}
                          disabled={!allowEdit}
                          onChange={changeEvent}
                          placeholder="Select event…"
                        />
                      </Field>

                      {showEventInfo && (
                        <InfoOverlay title="Trigger Event — ยิงตอนไหน" onClose={() => setShowEventInfo(false)}>
                          {(Object.values(HookEvents) as string[]).map(ev => {
                            const isCur = hook.event === ev;
                            return (
                              <div key={ev} style={{
                                padding: "9px 14px",
                                borderLeft: `3px solid ${isCur ? C.accent : "transparent"}`,
                                background: isCur ? C.accentDim : "transparent",
                              }}>
                                <div
                                  className={isCur ? undefined : "text-primary"}
                                  style={{ fontSize: "1.0rem", fontWeight: 700, marginBottom: 3, color: isCur ? C.accent : undefined }}
                                >
                                  {Utils.SplitByCase(ev)}
                                  {isCur && (
                                    <span className="text-muted" style={{ fontSize: "0.85em", fontWeight: 400, marginLeft: 8 }}>
                                      (เลือกอยู่)
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: "1.0rem", lineHeight: 1.55 }}>
                                  {Utils.GetHookEventInfo(ev)}
                                </div>
                              </div>
                            );
                          })}
                        </InfoOverlay>
                      )}

                      {hasEventFilter && (
                        <div style={{
                          background: C.bg, border: `1px solid ${C.border}`,
                          borderRadius: 10, padding: "14px 18px",
                          display: "flex", flexDirection: "column", gap: 10,
                          animation: "slideDown 0.2s ease",
                        }}>
                          <Field label="DocType Filter" hint="blank = every doc type">
                            <StyledInput
                              icon="⌗"
                              placeholder="PR, PO, INV — comma separated"
                              readOnly={!allowEdit}
                              value={eventFilter.docType ?? ""}
                              onChange={e => patchEventFilter({ docType: e.target.value })}
                            />
                          </Field>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={eventFilter.includeBot === true}
                                readOnly={!allowEdit}
                                onChange={e => allowEdit && patchEventFilter({ includeBot: e.target.checked })}
                              />
                            }
                            label="Include activity by bot"
                          />
                        </div>
                      )}

                      <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 12 }}>
                        <Field label="Method">
                          <EnumSelect
                            enumObj={HookMethods}
                            value={hook.method ?? ""}
                            disabled={!allowEdit}
                            onChange={v => setHook(h => ({ ...h, method: v as HookMethods }))}
                          />
                        </Field>
                        <Field label="Callback URL">
                          <StyledInput
                            icon="⇒"
                            placeholder="https://your-endpoint.com/webhook"
                            readOnly={!allowEdit}
                            value={hook.hookUrl ?? ""}
                            onChange={e => setHook(h => ({ ...h, hookUrl: e.target.value }))}
                          />
                        </Field>
                      </div>

                      <Field label="Authorization">
                        <EnumSelect
                          enumObj={HookAuths}
                          value={hook.authMethod ?? ""}
                          disabled={!allowEdit}
                          onChange={v => {
                            setHook(h => ({ ...h, authMethod: v as HookAuths, bearerToken: "" }));
                            setShowToken(false);
                          }}
                        />
                      </Field>

                      {/* Bearer Token panel */}
                      {isBearer && (
                        <div style={{
                          background: C.accentDim, border: `1px solid ${C.accent}33`,
                          borderRadius: 10, padding: "16px 18px",
                          animation: "slideDown 0.2s ease",
                        }}>
                          <Field label="Bearer Token" hint="stored securely">
                            <div style={{ position: "relative" }}>
                              <StyledInput
                                icon="🔑"
                                type={showToken ? "text" : "password"}
                                placeholder="eyJhbGciOiJIUzI1NiIs…"
                                value={hook.bearerToken ?? ""}
                                onChange={e => setHook(h => ({ ...h, bearerToken: e.target.value }))}
                                style={{ paddingRight: 44 }}
                                readOnly={!allowEdit}
                              />
                              <button
                                onClick={() => setShowToken(s => !s)}
                                style={{
                                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                                  background: "none", border: "none", cursor: "pointer", color: C.muted, /*fontSize: 14,*/ padding: 4,
                                }}
                                title={showToken ? "Hide" : "Show"}
                              >
                                {showToken ? "🙈" : "👁"}
                              </button>
                            </div>
                          </Field>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Header ── */}
                  {tab === "header" && (
                    <div style={{ animation: "slideDown 0.18s ease" }}>
                      <p style={{ fontSize: 12, color: C.muted, marginTop: 0, marginBottom: 20 }}>
                        HTTP headers sent with every request to the callback URL.<br />
                        <span className="text-primary">Content-Type</span> ca be &nbsp;
                        <span className="text-warning">application/x-www-form-urlencoded</span>&nbsp;or&nbsp;
                        <span className="text-warning">application/json</span>
                      </p>

                      <KVTable
                        rows={headers}
                        onUpdate={updateHeader}
                        onAdd={addHeader}
                        onRemove={removeHeader}
                        disabled={!allowEdit}
                        addLabel="Add Header"
                      />
                    </div>
                  )}

                  {/* ── Body ── */}
                  {tab === "body" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 22, animation: "slideDown 0.18s ease" }}>
                      <Field label="Body Type">
                        <BodyTypePicker
                          value={hook.bodyType ?? HookBodyTypes.None}
                          onChange={v => {
                            const newSection = v === HookBodyTypes.FormData ? HookDataSection.FormData : HookDataSection.Json;
                            setHook(h => ({ ...h, bodyType: v as HookBodyTypes }));
                            setBodyRows(r => r.map(row => ({ ...row, dataSection: newSection })));
                          }}
                        />
                      </Field>

                      {!hasBody ? (
                        <div style={{
                          textAlign: "center", padding: "36px 0", color: C.subtle,
                          fontSize: 12, border: `1px dashed ${C.border}`, borderRadius: 10,
                        }}>
                          Select <strong style={{ color: C.amber }}>Form Data</strong> or{" "}
                          <strong style={{ color: C.green }}>JSON</strong> to define body fields
                        </div>
                      ) : (
                        <div style={{ animation: "slideDown 0.18s ease" }}>
                          <p style={{ fontSize: 12, color: C.muted, marginTop: 0, marginBottom: 14 }}>
                            {availableFields.length > 0
                              ? "One property per field: pick one from the ▼ in any Value box, or type freely. {placeholders} are replaced when the hook fires."
                              : "Select a Trigger Event on the General tab to get the list of fields this hook can send."}
                          </p>
                          <KVTable
                            rows={bodyRows}
                            onUpdate={updateBodyRow}
                            onAdd={addBodyRow}
                            onRemove={removeBodyRow}
                            disabled={!allowEdit}
                            fields={availableFields}
                            addLabel={`Add ${hook.bodyType === HookBodyTypes.Json ? "JSON" : "Form"} Field`}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {tab === "test" && (
                    <div style={{ animation: "slideDown 0.18s ease" }}>

                      <div>
                        {testingHook && (<Loader />)}
                        {!testingHook && (
                          <ReactJson
                            src={testResult}
                            theme="monokai"
                            collapsed={false}
                            displayDataTypes={false}
                          />
                        )}
                      </div>
                    </div>
                  )}

                </AppSimpleBar>
              </div>

              {/* Footer actions */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, gap: 10 }}>
                <div style={{ display: "flex", gap: 10 }}>
                  {allowEdit && (
                    <button
                      onClick={fnDel}
                      style={{
                        background: C.redDim, border: `1px solid ${C.red}33`, borderRadius: 9,
                        color: C.red, cursor: "pointer", fontFamily: font, /*fontSize: 13,*/ fontWeight: 600,
                        padding: "10px 22px",
                      }}
                    >
                      Delete
                    </button>
                  )}
                  {allowEdit && (
                    <button
                      onClick={() => fnTestHook()}
                      style={{
                        background: C.amberDim, border: `1px solid ${C.amber}33`, borderRadius: 9,
                        color: C.amber, cursor: "pointer", fontFamily: font, fontWeight: 600,
                        padding: "10px 22px"
                      }}
                    >
                      {testingHook && (<LoadingIconWithText loadingText={""} />)}
                      Test
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => {
                      setHook({ method: HookMethods.POST, hookUrl: "", authMethod: HookAuths.None, bearerToken: "", purpose: "", event: null, bodyType: HookBodyTypes.None, jsonFilter: null });
                      setHeaders([]); setBodyRows([]);
                      onClose(-1);
                    }}
                    style={{
                      background: "none", border: `1px solid ${C.border}`, borderRadius: 9,
                      color: C.muted, cursor: "pointer", fontFamily: font, /*fontSize: 13,*/ fontWeight: 600,
                      padding: "10px 22px",
                    }}
                  >
                    Close
                  </button>
                  {allowEdit && (
                    <button
                      onClick={handleSave}
                      style={{
                        background: saved ? C.greenDim : C.accentDim,
                        border: `1px solid ${saved ? C.green : C.accent}`,
                        borderRadius: 9, color: saved ? C.green : C.accent,
                        cursor: "pointer", fontFamily: font, /*fontSize: 13,*/ fontWeight: 700,
                        padding: "10px 32px", transition: "all 0.25s", letterSpacing: "0.04em", minWidth: 130,
                      }}
                    >
                      {saved ? "✓ Saved!" : "Save Hook"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* 
        {!isLoading && (
          <div className="mt-1 pt-1" style={{ display: "flex", justifyContent: "space-between", gap: 8, borderTop: "dashed 1px var(--m2-line-divider-color)" }}>
            <div>
              {hookId && (<button className="btn btn-danger" onClick={fnDel} >ลบ</button>)}
            </div>
            <div className="d-flex justify-content-between" style={{ gap: "6px" }}>
              <button className="btn btn-warning" onClick={() => onClose(-1)} >ปิด</button>
              <button className="btn btn-primary" onClick={() => onSave()} >บันทึก</button>
            </div>
          </div>
        )} */}
        </div>
      </div >
    </ColorContext.Provider>,
    document.body
  );
}